/**
 * OperationHistoryFilters Component
 *
 * Composes SearchAndFilters compound component with all filter sub-components
 * for operation history filtering (event types, date range, author).
 */

import { type JSX, useCallback, useMemo, useState } from 'react';

import { Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { MemberFilterMultiSelect } from '@/components/common/MemberFilterMultiSelect';
import { ClearFiltersButton } from '@/components/ui/ClearFiltersButton';
import { FilterPillsList } from '@/components/ui/FilterPill';
import type { PillData } from '@/components/ui/FilterPill';
import { SearchAndFilters } from '@/components/ui/SearchAndFilters';
import { useLocalizedDate } from '@/hooks/useLocalizedDate';

import type {
  OperationHistoryConfig,
  OperationHistoryEvent,
  OperationHistoryFilters as OperationHistoryFiltersType,
} from '../OperationHistory.types';

import { OperationHistoryDateRangeFilter } from './OperationHistoryDateRangeFilter';
import { OperationHistoryEventTypesFilter } from './OperationHistoryEventTypesFilter';

type FilterUpdater =
  | OperationHistoryFiltersType
  | ((prev: OperationHistoryFiltersType) => OperationHistoryFiltersType);

/**
 * Pill summary value for a multi-value filter: the single selected label when
 * exactly one value is active, otherwise a "<count> <activeWord>" count badge.
 */
function formatCountPillValue(
  count: number,
  firstLabel: string | undefined,
  activeWord: string
): string {
  return count === 1 && firstLabel ? firstLabel : `${count} ${activeWord}`;
}

interface OperationHistoryFiltersProps<
  TEvent extends OperationHistoryEvent = OperationHistoryEvent,
> {
  filters: OperationHistoryFiltersType;
  setFilters: (updater: FilterUpdater) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  selectedAuthorLabels: string[];
  availableEventTypes: string[];
  config: OperationHistoryConfig<TEvent>;
  className?: string;
}

export function OperationHistoryFilters<
  TEvent extends OperationHistoryEvent = OperationHistoryEvent,
>({
  filters,
  setFilters,
  clearFilters,
  hasActiveFilters,
  selectedAuthorLabels,
  availableEventTypes,
  config,
  className,
}: OperationHistoryFiltersProps<TEvent>): JSX.Element {
  const { t } = useTranslation();
  const { formatDateCompact } = useLocalizedDate();
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  const hasAdvancedFilters =
    filters.eventTypes.length > 0 ||
    filters.dateFrom !== null ||
    filters.dateTo !== null ||
    filters.authorIds.length > 0;

  const handleToggleFilters = useCallback(() => {
    setFiltersExpanded((previous) => !previous);
  }, []);

  const handleSearchChange = useCallback(
    (value: string) => {
      setFilters((prev) => ({ ...prev, search: value }));
    },
    [setFilters]
  );

  const handleEventTypesChange = useCallback(
    (eventTypes: string[]) => {
      setFilters((prev) => ({ ...prev, eventTypes }));
    },
    [setFilters]
  );

  const handleDateFromChange = useCallback(
    (date: Date | string | null) => {
      const dateValue = typeof date === 'string' ? new Date(date) : date;
      setFilters((prev) => ({ ...prev, dateFrom: dateValue }));
    },
    [setFilters]
  );

  const handleDateToChange = useCallback(
    (date: Date | string | null) => {
      const dateValue = typeof date === 'string' ? new Date(date) : date;
      setFilters((prev) => ({ ...prev, dateTo: dateValue }));
    },
    [setFilters]
  );

  const handleAuthorsChange = useCallback(
    (authorIds: string[]) => {
      setFilters((prev) => ({ ...prev, authorIds }));
    },
    [setFilters]
  );

  const handleRemoveEventTypes = useCallback(() => {
    setFilters((prev) => ({ ...prev, eventTypes: [] }));
  }, [setFilters]);

  const handleRemoveDateRange = useCallback(() => {
    setFilters((prev) => ({ ...prev, dateFrom: null, dateTo: null }));
  }, [setFilters]);

  const handleRemoveAuthor = useCallback(() => {
    setFilters((prev) => ({ ...prev, authorIds: [] }));
  }, [setFilters]);

  const pills = useMemo((): readonly PillData[] => {
    const result: PillData[] = [];

    if (filters.eventTypes.length > 0) {
      const firstType = filters.eventTypes[0];
      const value = formatCountPillValue(
        filters.eventTypes.length,
        firstType ? config.getEventTypeLabel(firstType) : undefined,
        t('history.filters.activeTypes')
      );

      result.push({
        id: 'event-types',
        label: t('history.filters.eventTypesLabel'),
        value,
        onRemove: handleRemoveEventTypes,
      });
    }

    if (filters.dateFrom || filters.dateTo) {
      const fromText = filters.dateFrom
        ? formatDateCompact(filters.dateFrom)
        : t('filters.none');
      const toText = filters.dateTo
        ? formatDateCompact(filters.dateTo)
        : t('filters.none');

      result.push({
        id: 'date-range',
        label: t('history.filters.dateRangeLabel'),
        value: `${fromText} - ${toText}`,
        onRemove: handleRemoveDateRange,
      });
    }

    if (filters.authorIds.length > 0) {
      const value = formatCountPillValue(
        filters.authorIds.length,
        selectedAuthorLabels[0],
        t('history.filters.activeAuthors')
      );

      result.push({
        id: 'author',
        label: t('history.filters.authorLabel'),
        value,
        onRemove: handleRemoveAuthor,
      });
    }

    return result;
  }, [
    filters,
    t,
    formatDateCompact,
    config,
    selectedAuthorLabels,
    handleRemoveEventTypes,
    handleRemoveDateRange,
    handleRemoveAuthor,
  ]);

  return (
    <SearchAndFilters variant="compact" className={className}>
      <SearchAndFilters.Header>
        <SearchAndFilters.SearchBar
          placeholder={t('history.searchPlaceholder')}
          value={filters.search}
          onChange={handleSearchChange}
        />
        <SearchAndFilters.FilterToggle
          expanded={filtersExpanded}
          hasActiveFilters={hasAdvancedFilters || hasActiveFilters}
          onClick={handleToggleFilters}
          tooltip={t('filters.title')}
          aria-label={t('filters.title')}
        />
      </SearchAndFilters.Header>
      <SearchAndFilters.Section
        hasContent={filtersExpanded || hasAdvancedFilters}
      >
        <SearchAndFilters.Pills show={!filtersExpanded}>
          <FilterPillsList pills={pills} />
        </SearchAndFilters.Pills>
        <SearchAndFilters.FiltersPanel expanded={filtersExpanded}>
          <OperationHistoryEventTypesFilter
            selectedTypes={filters.eventTypes}
            availableTypes={availableEventTypes}
            getLabel={config.getEventTypeLabel}
            onChange={handleEventTypesChange}
          />
          <OperationHistoryDateRangeFilter
            dateFrom={filters.dateFrom}
            dateTo={filters.dateTo}
            onDateFromChange={handleDateFromChange}
            onDateToChange={handleDateToChange}
          />
          <MemberFilterMultiSelect
            value={filters.authorIds}
            onChange={handleAuthorsChange}
            label={t('history.author')}
            placeholder={t('history.authorPlaceholder')}
          />
          {hasAdvancedFilters && (
            <Group justify="center">
              <ClearFiltersButton onClick={clearFilters} />
            </Group>
          )}
        </SearchAndFilters.FiltersPanel>
      </SearchAndFilters.Section>
    </SearchAndFilters>
  );
}
